/**
 * Root validator for `flow_FlowUpdateResource`.
 * Required (per Swagger): name, defaultPaths.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type { VoluumFlowUpdate } from '../../voluum-flow.types';
import {
  FLOW_REALTIME_ROUTING_VALUES,
  OFFER_REDIRECT_MODE_VALUES,
} from '../enums';
import {
  type ValidationResult,
  VoluumPayloadValidationError,
} from '../errors';
import {
  isPlainObject,
  joinPath,
  pushError,
  requireString,
  type ValidationCtx,
} from '../primitives';
import { validatePathUpdate } from './path';
import { validatePathGroupUpdate } from './path-group';

function validateFlowOptionalEnums(
  ctx: ValidationCtx,
  v: Record<string, unknown>,
): void {
  if (v.defaultOfferRedirectMode !== undefined) {
    requireString(
      ctx,
      v,
      'defaultOfferRedirectMode',
      '',
      OFFER_REDIRECT_MODE_VALUES,
    );
  }
  if (v.realtimeRoutingApi !== undefined) {
    requireString(ctx, v, 'realtimeRoutingApi', '', FLOW_REALTIME_ROUTING_VALUES);
  }
  if (
    v.defaultPathsSmartRotation !== undefined &&
    typeof v.defaultPathsSmartRotation !== 'boolean'
  ) {
    pushError(
      ctx,
      'defaultPathsSmartRotation',
      'wrong_type',
      'Expected boolean',
    );
  }
}

function validateDefaultPaths(
  ctx: ValidationCtx,
  body: Record<string, unknown>,
): void {
  if (body.defaultPaths === undefined) {
    pushError(ctx, 'defaultPaths', 'missing_required', 'Missing required field "defaultPaths"');
    return;
  }
  if (!Array.isArray(body.defaultPaths)) {
    pushError(ctx, 'defaultPaths', 'invalid_array', 'Expected array');
    return;
  }
  body.defaultPaths.forEach((p, idx) => {
    validatePathUpdate(ctx, p, joinPath('defaultPaths', idx));
  });
}

function validateConditionalGroups(
  ctx: ValidationCtx,
  body: Record<string, unknown>,
): void {
  if (body.conditionalPathsGroups === undefined) return;
  if (!Array.isArray(body.conditionalPathsGroups)) {
    pushError(
      ctx,
      'conditionalPathsGroups',
      'invalid_array',
      'Expected array',
    );
    return;
  }
  body.conditionalPathsGroups.forEach((g, idx) => {
    validatePathGroupUpdate(ctx, g, joinPath('conditionalPathsGroups', idx));
  });
}

export function validateFlowUpdate(
  body: unknown,
): ValidationResult<VoluumFlowUpdate> {
  const ctx: ValidationCtx = { errors: [] };
  if (!isPlainObject(body)) {
    return {
      ok: false,
      errors: [
        {
          path: '',
          code: 'invalid_object',
          message: 'Expected flow update body to be an object',
        },
      ],
    };
  }
  requireString(ctx, body, 'name', '');
  validateDefaultPaths(ctx, body);
  validateConditionalGroups(ctx, body);
  validateFlowOptionalEnums(ctx, body);

  return ctx.errors.length === 0
    ? { ok: true, value: body as VoluumFlowUpdate }
    : { ok: false, errors: ctx.errors };
}

export function validateFlowUpdateOrThrow(body: unknown): VoluumFlowUpdate {
  const res = validateFlowUpdate(body);
  if (!res.ok) throw new VoluumPayloadValidationError(res.errors);
  return res.value;
}
